/**
 * What one pipeline stage says when it is opened: what it does, what came into it and where that went.
 *
 * Pure: it reads the counters the pipeline already shows (`FunnelCounters`) and divides each stage's input into
 * the parts the backend counted, so the figures agree with the Pipeline, the Alert Flow and the feed. A part that
 * is not counted is never invented: when the parts do not add up to the input, the difference is shown as
 * "not accounted for" instead of being hidden.
 *
 * It also says where each stage can be followed: the feed with a state or a type already chosen, the Alert Flow,
 * the sources. Dedup and Cluster + Score hold no records of their own to list (the feed starts at the threats),
 * so they are explained and sent to the flow, which draws what they held back.
 */
import type { FunnelCounters } from './funnel';
import { PIPELINE_STAGE_IDS, PIPELINE_STAGE_TITLES, type PipelineStageId, type StageTone } from './pipelineStages';

/**
 * What the Threat Feed can be opened on from a stage: the seven states of the client's definition 4 (notes 10/26), in the
 * order a threat goes through them, from the queue to the end.
 */
export type FeedStateFilter =
  | 'all'
  | 'unassigned'
  | 'assigned'
  | 'escalated'
  | 'pending_approval'
  | 'pending_execution'
  | 'closed'
  | 'failed';
export type FeedTypeFilter = 'all' | 'campaign' | 'incident';
export type FeedViewName = 'active' | 'suppressed';

/** What a filter ticks: the states and the types without "all", which is no choice at all. */
export type FeedStateGroup = Exclude<FeedStateFilter, 'all'>;
export type FeedType = Exclude<FeedTypeFilter, 'all'>;

export const FEED_STATE_FILTERS: readonly FeedStateFilter[] = [
  'all',
  'unassigned',
  'assigned',
  'escalated',
  'pending_approval',
  'pending_execution',
  'closed',
  'failed',
];
export const FEED_TYPE_FILTERS: readonly FeedTypeFilter[] = ['all', 'campaign', 'incident'];

export const FEED_STATE_CHOICES = FEED_STATE_FILTERS.filter((item): item is FeedStateGroup => item !== 'all');
export const FEED_TYPE_CHOICES = FEED_TYPE_FILTERS.filter((item): item is FeedType => item !== 'all');

export const FEED_STATE_LABEL: Record<FeedStateFilter, string> = {
  all: 'All states',
  unassigned: 'Unassigned',
  assigned: 'Assigned',
  escalated: 'Escalated',
  pending_approval: 'Pending approval',
  pending_execution: 'Pending execution',
  closed: 'Closed',
  failed: 'Failed',
};

export const FEED_TYPE_LABEL: Record<FeedTypeFilter, string> = {
  all: 'All types',
  campaign: 'Campaigns',
  incident: 'Lone incidents',
};

/** The feed as a stage leaves it: which list, and which rows of it. */
export interface FeedTarget {
  view?: FeedViewName;
  state?: FeedStateFilter;
  type?: FeedTypeFilter;
}

export type StageDestination =
  | { kind: 'feed'; label: string; target: FeedTarget; count?: number }
  | { kind: 'tab'; label: string; tab: 'flow' | 'src' };

/** How a part of the input is told apart: it went on, it was held back, it waits on a person, it failed. */
export type PartKind = 'on' | 'held' | 'need' | 'bad';

export interface StagePart {
  key: string;
  label: string;
  value: number;
  kind: PartKind;
  /** What happens to it, when the kind alone would say it wrong ("in progress" has not stopped). */
  note?: string;
}

export interface StageDetail {
  id: PipelineStageId;
  index: number;
  title: string;
  /** What the stage does, in plain words. */
  does: string;
  previous: string | null;
  next: string | null;
  /** What came into the stage, and what the parts are counted against. */
  input: { label: string; value: number };
  parts: StagePart[];
  /** Input that no part accounts for. Zero when the counts agree. */
  unaccounted: number;
  /** The tone of what waits on a person or failed here, with the count. */
  attention?: { tone: StageTone; count: number; text: string };
  destinations: StageDestination[];
}

const DOES: Record<PipelineStageId, string> = {
  ingest:
    'Reads the security events each connected source sends and puts them in one common shape, so the rest of the pipeline can compare them.',
  dedup:
    'Drops what has already been seen: the same alert arriving twice, or from two tools, counts once. Only unique candidates go on.',
  score:
    'Groups related alerts and scores how far each group is from what is normal. What looks like the usual baseline stops here.',
  classify:
    'An AI model reviews each anomaly with the context it retrieves (LLM and RAG) and rules it a threat, a false positive, or something a person has to judge.',
  consolidate:
    'Joins threats that share identities or assets into one campaign. A threat with nothing in common stays on its own, as a campaign of one.',
  plan:
    'Writes a remediation plan for each threat. A plan that needs approval waits on a person; the others can go on by themselves.',
  execute:
    'Runs the approved plans on the connected tools and checks the result. A plan that fails its check is rolled back and shows as reverted.',
};

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

export function buildStageDetail(id: PipelineStageId, c: FunnelCounters): StageDetail {
  const index = PIPELINE_STAGE_IDS.indexOf(id);
  const anomalies = c.verdicts.threat + c.verdicts.suppressed + c.verdicts.escalated;
  const started = c.actions.inProgress + c.actions.completed + c.actions.reverted + c.actions.cancelled;
  let input: StageDetail['input'];
  let parts: StagePart[];
  let attention: StageDetail['attention'];
  let destinations: StageDestination[];

  switch (id) {
    case 'ingest':
      input = { label: 'security events', value: c.events.total };
      parts = [
        { key: 'new', label: 'new', value: c.events.new, kind: 'on' },
        { key: 'duplicate', label: 'seen before', value: c.events.duplicate, kind: 'held' },
      ];
      if (c.candidates.error > 0) {
        attention = { tone: 'bad', count: c.candidates.error, text: `${plural(c.candidates.error, 'candidate', 'candidates')} failed to analyze` };
      }
      destinations = [{ kind: 'tab', label: 'Open Connected Sources', tab: 'src' }];
      break;
    case 'dedup':
      input = { label: 'candidates registered', value: c.candidates.registered };
      parts = [
        { key: 'unique', label: 'unique', value: c.candidates.unique, kind: 'on' },
        { key: 'duplicated', label: 'duplicated', value: c.candidates.duplicated, kind: 'held' },
        { key: 'inFlight', label: 'still being analyzed', value: c.candidates.pending + c.candidates.analyzing, kind: 'held', note: 'not finished yet' },
        { key: 'errors', label: 'failed', value: c.candidates.error, kind: 'bad' },
      ];
      destinations = [{ kind: 'tab', label: 'See the whole flow', tab: 'flow' }];
      break;
    case 'score':
      input = { label: 'unique candidates', value: c.candidates.unique };
      parts = [
        { key: 'anomalies', label: 'anomalies', value: anomalies, kind: 'on' },
        { key: 'baseline', label: 'baseline normal', value: c.verdicts.baseline, kind: 'held' },
      ];
      destinations = [{ kind: 'tab', label: 'See the whole flow', tab: 'flow' }];
      break;
    case 'classify':
      input = { label: 'anomalies', value: anomalies };
      parts = [
        { key: 'threats', label: 'threats', value: c.verdicts.threat, kind: 'on' },
        { key: 'suppressed', label: 'false positives suppressed', value: c.verdicts.suppressed, kind: 'held' },
        { key: 'escalated', label: 'escalated to a person', value: c.verdicts.escalated, kind: 'need' },
      ];
      if (c.verdicts.escalated > 0) {
        attention = { tone: 'need', count: c.verdicts.escalated, text: `${plural(c.verdicts.escalated, 'verdict waits', 'verdicts wait')} on a person` };
      }
      destinations = [
        {
          kind: 'feed',
          label: 'Show the resolved alerts',
          target: { view: 'suppressed' },
          count: c.verdicts.suppressed,
        },
        { kind: 'feed', label: 'Show the threats in the feed', target: { view: 'active' }, count: c.verdicts.threat },
      ];
      break;
    case 'consolidate':
      input = { label: 'threats', value: c.verdicts.threat };
      parts = [
        { key: 'inCampaign', label: `in ${plural(c.campaigns.total, 'campaign', 'campaigns')}`, value: c.threats.inCampaign, kind: 'on' },
        { key: 'standalone', label: 'on their own', value: c.threats.standalone, kind: 'held' },
      ];
      destinations = [
        {
          kind: 'feed',
          label: 'Show the campaigns in the feed',
          target: { view: 'active', type: 'campaign' },
          count: c.campaigns.total,
        },
        { kind: 'feed', label: 'Show the lone incidents', target: { view: 'active', type: 'incident' }, count: c.threats.standalone },
        { kind: 'tab', label: 'See the whole flow', tab: 'flow' },
      ];
      break;
    case 'plan':
      input = { label: 'remediations', value: c.actions.total };
      parts = [
        { key: 'pending', label: 'pending a decision', value: c.actions.pending, kind: 'need' },
        { key: 'confirmed', label: 'confirmed', value: c.actions.confirmed, kind: 'held', note: 'approved, waits to run' },
        { key: 'started', label: 'started or finished', value: started, kind: 'on' },
      ];
      if (c.actions.pending > 0) {
        attention = { tone: 'need', count: c.actions.pending, text: `${plural(c.actions.pending, 'plan waits', 'plans wait')} on a person` };
      }
      destinations = [
        {
          kind: 'feed',
          label: 'Show what awaits a decision',
          target: { view: 'active', state: 'pending_approval' },
          count: c.actions.pending,
        },
        { kind: 'tab', label: 'See the whole flow', tab: 'flow' },
      ];
      break;
    case 'execute':
      input = { label: 'plans started', value: started };
      parts = [
        { key: 'completed', label: 'completed', value: c.actions.completed, kind: 'on' },
        { key: 'inProgress', label: 'in progress', value: c.actions.inProgress, kind: 'held', note: 'still running' },
        { key: 'reverted', label: 'reverted', value: c.actions.reverted, kind: 'held', note: 'rolled back after its check' },
        { key: 'cancelled', label: 'cancelled', value: c.actions.cancelled, kind: 'held', note: 'stopped before the end' },
      ];
      destinations = [
        {
          kind: 'feed',
          label: 'Show what is pending execution',
          target: { view: 'active', state: 'pending_execution' },
          count: c.actions.inProgress,
        },
        {
          kind: 'feed',
          label: 'Show what is closed',
          target: { view: 'active', state: 'closed' },
          count: c.actions.completed,
        },
        {
          kind: 'feed',
          label: 'Show what failed',
          target: { view: 'active', state: 'failed' },
          count: c.actions.reverted + c.actions.cancelled,
        },
      ];
      break;
  }

  // A part that counts nothing says nothing, except the ones whose zero is information.
  const shown = parts.filter((part) => part.value > 0 || part.kind === 'on');
  const sum = shown.reduce((total, part) => total + part.value, 0);
  return {
    id,
    index: index + 1,
    title: PIPELINE_STAGE_TITLES[id],
    does: DOES[id],
    previous: index > 0 ? PIPELINE_STAGE_TITLES[PIPELINE_STAGE_IDS[index - 1]] : null,
    next: index < PIPELINE_STAGE_IDS.length - 1 ? PIPELINE_STAGE_TITLES[PIPELINE_STAGE_IDS[index + 1]] : null,
    input,
    parts: shown,
    unaccounted: Math.max(0, input.value - sum),
    ...(attention ? { attention } : {}),
    destinations,
  };
}
